/**
 * PendingDecisionStore - Data access for pending decisions
 *
 * Handles GraphQL operations for pending decisions in the notification system.
 */
import {
  getPendingDecisionCountQuery,
  getPendingDecisionsQuery,
  type PendingDecisionType,
  type RelationCandidateType,
  resolvePendingDecisionMutation,
  skipPendingDecisionMutation,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { GraphQLService } from '../../cloud';

// Type for a single pending decision
export type PendingDecision = PendingDecisionType;

// Type for a relation candidate
export type RelationCandidate = RelationCandidateType;

export class PendingDecisionStore extends Store {
  constructor(private readonly gqlService: GraphQLService) {
    super();
  }

  /**
   * Get all pending decisions for a workspace.
   */
  async getPendingDecisions(
    workspaceId: string,
    signal?: AbortSignal
  ): Promise<PendingDecision[]> {
    const result = await this.gqlService.gql({
      query: getPendingDecisionsQuery,
      variables: { workspaceId },
      context: { signal },
    });

    // Cast to the correct response type
    const response = result as {
      workspace?: { pendingDecisions: PendingDecision[] };
    };
    return response.workspace?.pendingDecisions ?? [];
  }

  /**
   * Get count of pending decisions for a workspace.
   */
  async getPendingDecisionCount(
    workspaceId: string,
    signal?: AbortSignal
  ): Promise<number> {
    const result = await this.gqlService.gql({
      query: getPendingDecisionCountQuery,
      variables: { workspaceId },
      context: { signal },
    });

    // Cast to the correct response type
    const response = result as { workspace?: { pendingDecisionCount: number } };
    return response.workspace?.pendingDecisionCount ?? 0;
  }

  /**
   * Resolve a pending decision by selecting a candidate.
   */
  async resolvePendingDecision(
    workspaceId: string,
    id: string,
    resolvedWith: string
  ): Promise<PendingDecision> {
    const result = await this.gqlService.gql({
      query: resolvePendingDecisionMutation,
      variables: { workspaceId, id, resolvedWith },
    });

    // Cast to the correct response type
    const response = result as { resolvePendingDecision: PendingDecision };
    return response.resolvePendingDecision;
  }

  /**
   * Skip a pending decision (leave relation unlinked).
   */
  async skipPendingDecision(
    workspaceId: string,
    id: string
  ): Promise<PendingDecision> {
    const result = await this.gqlService.gql({
      query: skipPendingDecisionMutation,
      variables: { workspaceId, id },
    });

    // Cast to the correct response type
    const response = result as { skipPendingDecision: PendingDecision };
    return response.skipPendingDecision;
  }
}
